import { DebugKey, canUseDOM, createDebug } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import { invert, isPlainObject, snakeCase } from 'lodash';

import { StatusCode } from '@swf/core/enums/statusCode';

import type { ResponseHeaders } from '../types/Request';

import { TXTWCL_Sorry___an_error_has_occurred } from './RequestError.messages.json';

const statusKeys = invert(StatusCode);

export interface ErrorBody {
  code: string;
  message?: string;
  text: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isErrorBody = (content: any): content is ErrorBody => !!content?.code;

export class RequestError<B = ErrorBody> extends Error {
  headers?: ResponseHeaders;
  body: B & { message: string };
  public readonly response?: Response;
  private service: string;
  private requestId?: string;

  constructor({
    service,
    body = {},
    response,
    method,
    headers,
    requestId,
    cause
  }: {
    service: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body: ErrorBody | any;
    response?: Response;
    method: string;
    headers?: ResponseHeaders;
    requestId?: string;
    cause?: ErrorOptions['cause'];
  }) {
    const statusCode = response?.status;
    const name = statusCode && statusCode >= 400 ? statusCode : method;

    if (isErrorBody(body)) {
      super(`${name} ${service} ${body.code}`, { cause });
    } else if (headers?.statusCode) {
      const { statusCode: errorCode } = headers;
      let displayCode = statusKeys[errorCode];

      if (!displayCode) {
        displayCode = snakeCase(errorCode as string).toUpperCase();
        if (isPlainObject(body)) {
          if (!body.message) body.message = TXTWCL_Sorry___an_error_has_occurred.defaultMessage;
        } else {
          body = {
            code: displayCode,
            message: body
          };
        }
      }

      super(`${name} ${service} ${displayCode}`, { cause });
    } else {
      super(`${name} ${service}`, { cause });
    }

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'request-error',
      message: this.message,
      data: {
        method,
        service,
        statusCode: response?.status
      }
    });

    // https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    Object.setPrototypeOf(this, RequestError.prototype);

    this.name = 'RequestError';
    this.body = body;
    this.response = response;
    this.service = service;
    this.requestId = requestId;

    if (headers) this.headers = headers;
    else if (statusCode) this.headers = { statusCode };
  }

  log() {
    const debug = createDebug(DebugKey.Request, this.requestId);
    debug('failed %s %s', this.service, this.message.replace(` ${this.service}`, ''));
    if (process.env.NODE_ENV !== 'test' && (!this.response || this.response.ok) && canUseDOM) {
      // eslint-disable-next-line no-console
      console.error(this);
    }
  }
}

export const isRequestErrorWithCode = (err: unknown): err is RequestError<{ code: string }> =>
  err instanceof RequestError && 'code' in err.body;
